import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { API_ENDPOINTS } from '../lib/api'
import {
  FiSend,
  FiLoader,
  FiCheck,
  FiX,
  FiSearch,
  FiChevronLeft,
  FiChevronRight,
  FiSidebar,
  FiMessageSquare,
  FiUsers,
  FiFileText,
  FiClock,
  FiRefreshCw,
  FiTrash2,
  FiZap,
  FiEye,
  FiBarChart2,
  FiEdit2,
  FiTarget,
  FiDownload,
  FiAlertCircle,
  FiFolder,
  FiChevronDown,
} from 'react-icons/fi'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts'
import * as XLSX from 'xlsx'
import { MetaAdsPanel } from './MetaAdsPanel'
import { MarketingLiveDashboard } from './MarketingLiveDashboard'
import { CHART_COLORS, GRID } from '../lib/chartTheme'

// ─── Types ───
interface MarketingSession {
  id: string
  title: string
  status: string
  campaign_id: number | null
  folder: string | null
  created_at: string
  updated_at: string
  contacts_total?: number
  contacts_success?: number
  contacts_failed?: number
  campaign_status?: string
  // Praça Planalto — catálogo de disparos (ver loadSessions)
  is_test?: boolean
  template_name?: string
  delivered?: number
  read?: number
}



interface Contact {
  name: string
  phone: string
  extras?: Record<string, string>
}


interface CampaignReportData {
  campaign: {
    id: string
    status: string
    templateName: string
    messageType: string
    platform: string
    delayMs: number
    startedAt: string | null
    completedAt: string | null
    createdAt: string
  }
  metrics: {
    total: number; sent: number; success: number; failed: number
    delivered: number; read: number; responses: number
    successRate: number; deliveryRate: number; readRate: number; responseRate: number
  }
  statusDistribution: Record<string, number>
  crosscheck?: {
    porDistrito: { nome: string; total: number; entregue: number; lido: number; respondeu: number }[]
    porGrupo: { nome: string; total: number; entregue: number; lido: number; respondeu: number }[]
    qualidade: { total: number; identificados: number; ambiguos: number; semCadastro: number }
  }
  contacts: {
    phone: string; contactName: string; status: string; latestStatus: string
    errorMessage: string | null; sentAt: string | null
    deliveredAt: string | null; readAt: string | null
    responseContent: string | null; respondedAt: string | null
    codigo?: string | null; cadastroNome?: string | null; grupo?: string | null
    distrito?: string | null; cidade?: string | null; nivel?: string | null; tipoConsultor?: string | null
    matchAmbiguo?: boolean; semCadastro?: boolean
  }[]
}

type CenterAction = 'show_templates' | 'execute_campaign' | 'show_report' |
  'show_audience' | 'campaign_report' | null

interface CenterContent {
  action: CenterAction
  data: Record<string, unknown>
}

// paleta unica e validada: ver lib/chartTheme.ts

export function MarketingPanel() {
  const { authenticatedFetch } = useAuth()

  // ─── Top-level tab ───
  // Repaginada 2026-07-15: painel virou ANALYTICS-only (disparo saiu da UI —
  // rotas/wizard seguem no código, dormentes; disparos são feitos fora daqui).
  //   - 'aovivo': visão de senior de tráfego (Meta × CTWA × leitura de disparos + IA)
  //   - 'relatorios': analisar campanhas passadas (lista + KPI + tabela + IA)
  //   - 'meta': anúncios (campanhas, atribuição, eficiência, comparação)
  // 'disparo' permanece no union só pra não tocar o código dormente do wizard.
  //
  // PRAÇA PLANALTO (2026-08-24): abre em 'relatorios', não em 'aovivo'. Aqui o
  // painel existe só para CATALOGAR os disparos (entrega, leitura, resposta,
  // contato a contato) — 'aovivo' depende do MarketingLiveDashboard, que puxa
  // Meta Ads, e o sync de ads está desligado nesta praça (META_ADS_SYNC=0, a ad
  // account do lib/meta-ads.cjs é a da matriz). Deixar o default 'aovivo'
  // abriria o painel numa tela que não tem como preencher.
  const [activeTab] = useState<'aovivo' | 'disparo' | 'relatorios' | 'meta'>('relatorios')

  // ─── Sidebar State ───
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [sessions, setSessions] = useState<MarketingSession[]>([])
  const [sessionsLoading, setSessionsLoading] = useState(false)
  const [sidebarFilter, setSidebarFilter] = useState('')

  // ─── Center State ───
  const [centerContent, setCenterContent] = useState<CenterContent | null>(null)
  const [campaignReport, setCampaignReport] = useState<CampaignReportData | null>(null)
  const [reportLoading, setReportLoading] = useState(false)
  const [reportTab, setReportTab] = useState<'sintetico' | 'analitico'>('sintetico')

  // ─── Session Management State ───
  // Catálogo de disparos (praça Planalto): dias com os testes abertos e
  // renomeação inline do card.
  const [diasComTesteAberto, setDiasComTesteAberto] = useState<string[]>([])
  const [renomeando, setRenomeando] = useState<string | null>(null)
  const [renomeandoValor, setRenomeandoValor] = useState('')
  const [sessionMenu, setSessionMenu] = useState<string | null>(null)
  const [renamingSession, setRenamingSession] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState('')
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; campaign?: { templateName: string; contactsTotal: number; status: string } } | null>(null)
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null)
  const [folderInput, setFolderInput] = useState('')
  const [showNewFolder, setShowNewFolder] = useState(false)



  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null)

  // ─── Report AI Summary ───
  const [reportSummary, setReportSummary] = useState<string | null>(null)
  const [reportSummaryLoading, setReportSummaryLoading] = useState(false)

  // PRAÇA PLANALTO: a lista NÃO vem de marketing_sessions.
  //
  // Sessão só nasce quando a campanha é criada pelo wizard do painel — e aqui o
  // disparo é feito por scripts/disparar-campanha.cjs (o painel é analytics-only
  // desde 15/07/2026). Lendo sessions, a tela abriria vazia mesmo com campanha
  // enviada. Então a fonte é a própria campaign_queue, via scope=all para ser
  // catálogo COMPARTILHADO: a campanha disparada por um operador precisa
  // aparecer para a Rose e as consultoras, não só para quem apertou o botão.
  // O formato é adaptado para MarketingSession, e todo o resto do painel
  // (sidebar, cards, relatório) segue funcionando sem saber da troca.
  const loadSessions = useCallback(async () => {
    setSessionsLoading(true)
    try {
      const r = await authenticatedFetch('/api/campaign-queue?scope=all&limit=100')
      const d = await r.json()
      if (d.success) {
        setSessions((d.campaigns || []).map((c: {
          id: string; templateName?: string; status?: string; total?: number
          success?: number; failed?: number; createdAt: string; completedAt?: string | null
          label?: string | null; isTest?: boolean; delivered?: number; read?: number
        }): MarketingSession => ({
          id: c.id,
          // label é o nome dado no disparo. Cai no template só para campanhas
          // antigas, anteriores à coluna — nunca para disparo novo (o script exige).
          title: c.label || c.templateName || 'Campanha',
          is_test: !!c.isTest,
          template_name: c.templateName,
          delivered: c.delivered || 0,
          read: c.read || 0,
          status: c.status || 'completed',
          // campaign_id carrega o id da campanha (string aqui, number no tipo
          // herdado da matriz) — é o que loadCampaignReport() consome.
          campaign_id: c.id as unknown as number,
          folder: null,
          created_at: c.createdAt,
          updated_at: c.completedAt || c.createdAt,
          contacts_total: c.total || 0,
          contacts_success: c.success || 0,
          contacts_failed: c.failed || 0,
          campaign_status: c.status || 'completed',
        })))
      }
    } catch { /* */ }
    finally { setSessionsLoading(false) }
  }, [authenticatedFetch])

  // Renomeia a campanha no catálogo. Otimista na tela e reconciliado pelo
  // loadSessions — se o PATCH falhar, o recarregamento devolve o nome antigo.
  const renomearCampanha = useCallback(async (campaignId: string, novoNome: string) => {
    const nome = novoNome.trim()
    setRenomeando(null)
    if (!nome) return
    setSessions(prev => prev.map(s => (s.id === campaignId ? { ...s, title: nome } : s)))
    try {
      await authenticatedFetch(`/api/campaign-queue/${campaignId}/label`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ label: nome }),
      })
    } catch { /* */ }
    loadSessions()
  }, [authenticatedFetch, loadSessions])







  // ─── Session Management ───
  const deleteSession = useCallback(async (sessionId: string, confirm = false) => {
    try {
      const r = await authenticatedFetch(`${API_ENDPOINTS.MARKETING_SESSIONS}/${sessionId}?confirm=${confirm}`, { method: 'DELETE' })
      const d = await r.json()
      if (d.requiresConfirmation) {
        setDeleteConfirm({ id: sessionId, campaign: d.campaign })
        return
      }
      if (d.success) {
        setSessions(p => p.filter(s => s.id !== sessionId))
        if (currentSessionId === sessionId) {
          setCenterContent(null)
          setCurrentSessionId(null)
        }
        setDeleteConfirm(null)
        setSessionMenu(null)
      }
    } catch {}
  }, [authenticatedFetch, currentSessionId])

  const renameSession = useCallback(async (sessionId: string, newTitle: string) => {
    if (!newTitle.trim()) return
    try {
      await authenticatedFetch(`${API_ENDPOINTS.MARKETING_SESSIONS}/${sessionId}`, {
        method: 'PATCH', body: JSON.stringify({ title: newTitle.trim() })
      })
      setSessions(p => p.map(s => s.id === sessionId ? { ...s, title: newTitle.trim() } : s))
      setRenamingSession(null)
    } catch {}
  }, [authenticatedFetch])

  const moveToFolder = useCallback(async (sessionId: string, folder: string | null) => {
    try {
      await authenticatedFetch(`${API_ENDPOINTS.MARKETING_SESSIONS}/${sessionId}`, {
        method: 'PATCH', body: JSON.stringify({ folder })
      })
      setSessions(p => p.map(s => s.id === sessionId ? { ...s, folder } : s))
      setSessionMenu(null)
    } catch {}
  }, [authenticatedFetch])



  const loadCampaignReport = useCallback(async (campaignId: string) => {
    setReportLoading(true)
    setReportTab('sintetico')
    setReportSummary(null)
    try {
      const r = await authenticatedFetch(`/api/campaign-queue/${campaignId}/report`)
      const d = await r.json()
      if (d.success) {
        setCampaignReport(d as CampaignReportData)
        setCenterContent({ action: 'campaign_report', data: {} })
      }
    } catch { /* */ }
    finally { setReportLoading(false) }
  }, [authenticatedFetch])

  // ─── Load data on mount ───
  useEffect(() => {
    loadSessions()
  }, [])




  const downloadReportPdf = useCallback(() => {
    if (!campaignReport) return
    const { campaign: c, metrics: m, contacts } = campaignReport

    const statusLabel = (s: string) => {
      switch (s) { case 'sent': return 'Enviado'; case 'delivered': return 'Entregue'; case 'read': return 'Lido'; case 'failed': return 'Falha'; case 'pending': return 'Pendente'; default: return s }
    }
    const fmtDate = (d: string | null) => d ? new Date(d).toLocaleString('pt-BR') : '-'

    // Build HTML for PDF
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Relatório - ${c.templateName}</title>
    <style>
      body { font-family: 'Segoe UI', Arial, sans-serif; margin: 40px; color: #1a1a2e; font-size: 12px; }
      h1 { color: #7c3aed; font-size: 20px; margin-bottom: 4px; }
      h2 { color: #374151; font-size: 14px; margin-top: 24px; margin-bottom: 8px; border-bottom: 2px solid #7c3aed; padding-bottom: 4px; }
      .subtitle { color: #6b7280; font-size: 11px; margin-bottom: 20px; }
      .kpis { display: flex; gap: 12px; flex-wrap: wrap; margin: 16px 0; }
      .kpi { flex: 1; min-width: 100px; background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 8px; padding: 12px; text-align: center; }
      .kpi-value { font-size: 22px; font-weight: 700; }
      .kpi-label { font-size: 9px; color: #6b7280; text-transform: uppercase; letter-spacing: 1px; margin-top: 2px; }
      .kpi-rate { font-size: 10px; color: #6b7280; margin-top: 2px; }
      table { width: 100%; border-collapse: collapse; margin-top: 8px; font-size: 10px; }
      th { background: #f3f4f6; padding: 6px 8px; text-align: left; font-weight: 600; border-bottom: 2px solid #e5e7eb; }
      td { padding: 5px 8px; border-bottom: 1px solid #f3f4f6; }
      tr:hover { background: #faf5ff; }
      .status-sent { color: #3b82f6; } .status-delivered { color: #10b981; } .status-read { color: #7c3aed; }
      .status-failed { color: #ef4444; } .status-pending { color: #9ca3af; }
      .info-row { display: flex; justify-content: space-between; padding: 4px 0; border-bottom: 1px solid #f3f4f6; }
      .info-label { color: #6b7280; } .info-value { font-weight: 600; }
      @media print { body { margin: 20px; } }
    </style></head><body>
    <h1>Relatório de Campanha</h1>
    <div class="subtitle">${c.templateName} — ${fmtDate(c.createdAt)}</div>

    <h2>Informações da Campanha</h2>
    <div class="info-row"><span class="info-label">Template</span><span class="info-value">${c.templateName}</span></div>
    <div class="info-row"><span class="info-label">Plataforma</span><span class="info-value">${(c.platform || 'meta').toUpperCase()}</span></div>
    <div class="info-row"><span class="info-label">Status</span><span class="info-value">${c.status === 'completed' ? 'Concluída' : c.status === 'failed' ? 'Falhou' : c.status}</span></div>
    <div class="info-row"><span class="info-label">Início</span><span class="info-value">${fmtDate(c.startedAt)}</span></div>
    <div class="info-row"><span class="info-label">Fim</span><span class="info-value">${fmtDate(c.completedAt)}</span></div>

    <h2>Métricas</h2>
    <div class="kpis">
      <div class="kpi"><div class="kpi-value" style="color:#6b7280">${m.total}</div><div class="kpi-label">Total</div></div>
      <div class="kpi"><div class="kpi-value" style="color:#3b82f6">${m.sent}</div><div class="kpi-label">Enviados</div><div class="kpi-rate">${m.successRate}%</div></div>
      <div class="kpi"><div class="kpi-value" style="color:#10b981">${m.delivered}</div><div class="kpi-label">Entregues</div><div class="kpi-rate">${m.deliveryRate}%</div></div>
      <div class="kpi"><div class="kpi-value" style="color:#7c3aed">${m.read}</div><div class="kpi-label">Lidos</div><div class="kpi-rate">${m.readRate}%</div></div>
      <div class="kpi"><div class="kpi-value" style="color:#f59e0b">${m.responses}</div><div class="kpi-label">Respostas</div><div class="kpi-rate">${m.responseRate}%</div></div>
      <div class="kpi"><div class="kpi-value" style="color:#ef4444">${m.failed}</div><div class="kpi-label">Falhas</div></div>
    </div>

    <h2>Detalhamento por Contato (${contacts.length})</h2>
    <table>
      <thead><tr><th>#</th><th>Consultor</th><th>Cód.</th><th>Grupo</th><th>Distrito</th><th>Telefone</th><th>Status</th><th>Enviado</th><th>Entregue</th><th>Lido</th></tr></thead>
      <tbody>${contacts.map((ct, i) => `<tr>
        <td>${i + 1}</td>
        <td>${ct.semCadastro ? '<span style="color:#ef4444">sem cadastro</span>' : ((ct.cadastroNome || '-') + (ct.matchAmbiguo ? ' <span style="color:#f59e0b">(ambíguo)</span>' : ''))}</td>
        <td>${ct.codigo || '-'}</td><td>${ct.grupo || '-'}</td><td>${ct.distrito || '-'}</td>
        <td>${ct.phone}</td>
        <td class="status-${ct.latestStatus || ct.status}">${statusLabel(ct.latestStatus || ct.status)}</td>
        <td>${fmtDate(ct.sentAt)}</td><td>${fmtDate(ct.deliveredAt)}</td><td>${fmtDate(ct.readAt)}</td>
      </tr>`).join('')}</tbody>
    </table>
    ${campaignReport.crosscheck ? (() => {
      const cc = campaignReport.crosscheck
      const q = cc.qualidade
      const pct = (n: number) => q.total > 0 ? Math.round((n / q.total) * 100) : 0
      const tbl = (rows: { nome: string; total: number; entregue: number; lido: number; respondeu: number }[]) =>
        `<table><thead><tr><th>Nome</th><th>Disparos</th><th>Entregue</th><th>Lido</th><th>Resp.</th></tr></thead><tbody>${rows.map(r => `<tr><td>${r.nome}</td><td>${r.total}</td><td>${r.entregue || '-'}</td><td>${r.lido || '-'}</td><td>${r.respondeu || '-'}</td></tr>`).join('')}</tbody></table>`
      return `
    <h2>Crosscheck — Identificação dos Destinatários</h2>
    <div class="kpis">
      <div class="kpi"><div class="kpi-value" style="color:#6b7280">${q.total}</div><div class="kpi-label">Total</div></div>
      <div class="kpi"><div class="kpi-value" style="color:#10b981">${q.identificados}</div><div class="kpi-label">Identificados</div><div class="kpi-rate">${pct(q.identificados)}%</div></div>
      <div class="kpi"><div class="kpi-value" style="color:#f59e0b">${q.ambiguos}</div><div class="kpi-label">Ambíguos</div><div class="kpi-rate">${pct(q.ambiguos)}%</div></div>
      <div class="kpi"><div class="kpi-value" style="color:#ef4444">${q.semCadastro}</div><div class="kpi-label">Sem cadastro</div><div class="kpi-rate">${pct(q.semCadastro)}%</div></div>
    </div>
    <h2>Por Distrito</h2>${tbl(cc.porDistrito)}
    <h2>Por Grupo</h2>${tbl(cc.porGrupo)}`
    })() : ''}
    </body></html>`

    const blob = new Blob([html], { type: 'text/html' })
    const url = URL.createObjectURL(blob)
    const win = window.open(url, '_blank')
    if (win) {
      win.onload = () => { setTimeout(() => { win.print(); URL.revokeObjectURL(url) }, 500) }
    }
  }, [campaignReport])

  // Export XLSX (client-side, SheetJS) — detalhado + dados sanitizados.
  // Abas: Resumo · Contatos · Ambíguos · Sem Cadastro · Por Distrito · Por Grupo.
  const downloadReportXlsx = useCallback(() => {
    if (!campaignReport) return
    const { campaign: c, metrics: m, contacts, crosscheck } = campaignReport

    // ── Sanitizadores ──
    const clean = (v: unknown) => String(v ?? '').replace(/\s+/g, ' ').trim()
    const fmtDt = (d: string | null) => d ? new Date(d).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''
    const MINUSC = new Set(['de', 'da', 'do', 'das', 'dos', 'e'])
    const titleCase = (s: string) => clean(s).toLowerCase().replace(/[a-zà-ú][^\s]*/g, (w, idx) =>
      (idx > 0 && MINUSC.has(w)) ? w : w.charAt(0).toUpperCase() + w.slice(1))
    const STATUS_PT: Record<string, string> = { sent: 'Enviado', delivered: 'Entregue', read: 'Lido', failed: 'Falha', pending: 'Pendente' }
    const statusPt = (s: string) => STATUS_PT[s] || s || ''
    const NIVEL: Record<string, string> = { '0': 'Distribuidor', '1': 'Empresário', '2': 'Líder', '3': 'Consultora' }
    const nivelPt = (n?: string | null) => (n != null && NIVEL[String(n)]) || ''
    const fmtFone = (raw: string) => {
      const d = String(raw || '').replace(/\D/g, '')
      let n = d
      if (n.length === 13 && n.startsWith('55')) n = n.slice(2)       // tira DDI
      else if (n.length === 12 && n.startsWith('55')) n = n.slice(2)
      if (n.length === 11) return `(${n.slice(0, 2)}) ${n.slice(2, 7)}-${n.slice(7)}`
      if (n.length === 10) return `(${n.slice(0, 2)}) ${n.slice(2, 6)}-${n.slice(6)}`
      return d || ''
    }
    // Código/Telefone vão como string → SheetJS escreve célula de texto (t:'s'),
    // então o Excel não converte pra número nem mostra notação científica.

    const wb = XLSX.utils.book_new()

    // ── Resumo ──
    const pct = (n: number, base: number) => base > 0 ? `${Math.round((n / base) * 100)}%` : '0%'
    const resumo: (string | number)[][] = [
      ['RELATÓRIO DE CAMPANHA'],
      [],
      ['Template', clean(c.templateName)],
      ['Plataforma', (c.platform || 'meta').toUpperCase()],
      ['Status', c.status === 'completed' ? 'Concluída' : c.status === 'failed' ? 'Falhou' : clean(c.status)],
      ['Criada em', fmtDt(c.createdAt)],
      ['Início', fmtDt(c.startedAt)],
      ['Fim', fmtDt(c.completedAt)],
      [],
      ['MÉTRICAS', 'Qtd', '%'],
      ['Total', m.total, ''],
      ['Enviados', m.sent, `${m.successRate}%`],
      ['Entregues', m.delivered, `${m.deliveryRate}%`],
      ['Lidos', m.read, `${m.readRate}%`],
      ['Respostas', m.responses, `${m.responseRate}%`],
      ['Falhas', m.failed, ''],
    ]
    if (crosscheck) {
      const q = crosscheck.qualidade
      resumo.push([], ['IDENTIFICAÇÃO (CROSSCHECK)', 'Qtd', '%'],
        ['Total disparado', q.total, ''],
        ['Identificados', q.identificados, pct(q.identificados, q.total)],
        ['Ambíguos (linha compartilhada)', q.ambiguos, pct(q.ambiguos, q.total)],
        ['Sem cadastro', q.semCadastro, pct(q.semCadastro, q.total)])
    }
    const wsResumo = XLSX.utils.aoa_to_sheet(resumo)
    wsResumo['!cols'] = [{ wch: 30 }, { wch: 22 }, { wch: 10 }]
    XLSX.utils.book_append_sheet(wb, wsResumo, 'Resumo')

    // ── builder de linha de contato (detalhado + sanitizado) ──
    const linha = (ct: typeof contacts[number], i: number) => ({
      '#': i + 1,
      Consultor: ct.semCadastro ? '' : titleCase(ct.cadastroNome || ''),
      'Código': clean(ct.codigo),
      'Nível': nivelPt(ct.nivel),
      Grupo: titleCase(ct.grupo || ''),
      Distrito: titleCase(ct.distrito || ''),
      Cidade: titleCase(ct.cidade || ''),
      Telefone: fmtFone(ct.phone),
      Identificação: ct.semCadastro ? 'Sem cadastro' : ct.matchAmbiguo ? 'Ambíguo' : 'OK',
      Status: statusPt(ct.latestStatus || ct.status),
      Enviado: fmtDt(ct.sentAt),
      Entregue: fmtDt(ct.deliveredAt),
      Lido: fmtDt(ct.readAt),
      Resposta: clean(ct.responseContent),
      Erro: clean(ct.errorMessage),
    })
    const colsContato = [
      { wch: 5 }, { wch: 30 }, { wch: 12 }, { wch: 12 }, { wch: 18 }, { wch: 18 },
      { wch: 18 }, { wch: 18 }, { wch: 13 }, { wch: 10 }, { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 30 }, { wch: 24 },
    ]
    const mkSheet = (rows: typeof contacts, name: string) => {
      const ws = XLSX.utils.json_to_sheet(rows.map(linha))
      ws['!cols'] = colsContato
      ws['!autofilter'] = { ref: ws['!ref'] || 'A1' }
      XLSX.utils.book_append_sheet(wb, ws, name)
    }

    // ── Contatos (todos) + abas de detalhe ──
    mkSheet(contacts, 'Contatos')
    const ambiguos = contacts.filter(ct => ct.matchAmbiguo)
    const semCad = contacts.filter(ct => ct.semCadastro)
    if (ambiguos.length) mkSheet(ambiguos, 'Ambíguos')
    if (semCad.length) mkSheet(semCad, 'Sem Cadastro')

    // ── Quebras por distrito / grupo ──
    if (crosscheck) {
      const aggRows = (rows: { nome: string; total: number; entregue: number; lido: number; respondeu: number }[]) =>
        rows.map(r => ({ Nome: titleCase(r.nome), Disparos: r.total, Entregue: r.entregue, Lido: r.lido, Respondeu: r.respondeu }))
      const wsD = XLSX.utils.json_to_sheet(aggRows(crosscheck.porDistrito))
      wsD['!cols'] = [{ wch: 22 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 10 }]
      XLSX.utils.book_append_sheet(wb, wsD, 'Por Distrito')
      const wsG = XLSX.utils.json_to_sheet(aggRows(crosscheck.porGrupo))
      wsG['!cols'] = [{ wch: 22 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 10 }]
      XLSX.utils.book_append_sheet(wb, wsG, 'Por Grupo')
    }

    const safe = clean(c.templateName || 'campanha').replace(/[^a-z0-9_-]+/gi, '_').slice(0, 40)
    const stamp = new Date().toISOString().slice(0, 10)
    XLSX.writeFile(wb, `relatorio_${safe}_${stamp}.xlsx`)
  }, [campaignReport])

  // Resume IA — chama o endpoint bounded e mostra parágrafo
  const summarizeReport = useCallback(async () => {
    if (!campaignReport || reportSummaryLoading) return
    setReportSummary(null)
    setReportSummaryLoading(true)
    try {
      const r = await authenticatedFetch('/api/marketing/ai/summarize-campaign', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ campaignId: campaignReport.campaign.id }),
      })
      const d = await r.json()
      setReportSummary(d.summary || d.error || 'IA não retornou resumo.')
    } catch {
      setReportSummary('Erro de rede ao resumir.')
    } finally { setReportSummaryLoading(false) }
  }, [campaignReport, authenticatedFetch, reportSummaryLoading])







  const formatDate = (d: string) => new Date(d).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })

  // ─── Sidebar content filtering ───
  const filteredSessions = sidebarFilter
    ? sessions.filter(s => s.title.toLowerCase().includes(sidebarFilter.toLowerCase()))
    : sessions



  // ═══════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════
  // Estrutura: TabBar topo + corpo. Em 'meta' o corpo é placeholder
  // full-width (sem sidebar). Em 'disparo'/'relatorios' mantém layout
  // sidebar+centro herdado, mas conteúdo da sidebar muda por aba.
  return (
    <div className="flex flex-col h-full text-foreground">

      {/* Painel ÚNICO (2026-07-15): tab bar removida — 'aovivo' é o default e
          não há como navegar pras abas dormentes (relatorios/meta/disparo). O
          conteúdo delas vive agora no drill-down do MarketingLiveDashboard. */}
      {/* ═══ AO VIVO / META TABS (full-width, sem sidebar) ═══ */}
      {activeTab === 'aovivo' ? (
        <MarketingLiveDashboard />
      ) : activeTab === 'meta' ? (
        <MetaAdsPanel />
      ) : (

      <div className="flex flex-1 min-h-0">

      {/* ═══ LEFT SIDEBAR (collapsible) ═══ */}
      {sidebarOpen ? (
        <div className="w-72 border-r border-border flex flex-col bg-card neu-bar shrink-0 relative">
          {/* Collapse button */}
          <button onClick={() => setSidebarOpen(false)}
            className="absolute -right-3 top-1/2 -translate-y-1/2 z-20 w-6 h-6 rounded-full bg-card neu-convex border border-border flex items-center justify-center hover:bg-secondary transition-colors shadow-sm"
            title="Recolher painel">
            <FiChevronLeft className="w-3 h-3 text-muted-foreground" />
          </button>

          {/* Header */}
          <div className="p-3 border-b border-border">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-sm">
                <FiTarget className="w-3.5 h-3.5 text-white" />
              </div>
              <div>
                <div className="text-xs font-semibold text-foreground leading-none">
                  Relatórios
                </div>
                <div className="text-[10px] text-muted-foreground leading-tight mt-0.5">
                  Histórico de campanhas
                </div>
              </div>
            </div>
          </div>

          {/* Filter */}
          <div className="p-2 border-b border-border">
            <div className="relative">
              <FiSearch className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
              <input type="text" placeholder="Filtrar..." value={sidebarFilter} onChange={e => setSidebarFilter(e.target.value)}
                className="w-full pl-6 pr-2 py-1 text-[11px] bg-background border border-border rounded focus:outline-none focus:ring-1 focus:ring-violet-500" />
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {/* Sessions list — renderizada apenas na aba Relatórios.
                Em Disparo a sidebar mostra Templates/Audiências. */}
            {activeTab === 'relatorios' && (
              sessionsLoading ? (
                <div className="flex items-center justify-center py-8"><FiLoader className="w-4 h-4 animate-spin text-muted-foreground" /></div>
              ) : filteredSessions.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
                  <FiMessageSquare className="w-6 h-6 text-muted-foreground/30 mb-2" />
                  <span className="text-[11px] text-muted-foreground">Nenhuma sessao ainda</span>
                  <span className="text-[10px] text-muted-foreground/60 mt-0.5">Clique em + para iniciar uma campanha</span>
                </div>
              ) : (() => {
                // Agrupar por pasta
                const folders = [...new Set(filteredSessions.map(s => s.folder).filter(Boolean))] as string[]
                const noFolder = filteredSessions.filter(s => !s.folder)

                const renderSession = (session: MarketingSession) => (
                  <div key={session.id}
                    className={`relative w-full text-left px-3 py-2 border-b border-border hover:bg-secondary/50 transition-colors group ${
                      currentSessionId === session.id ? 'bg-violet-500/10 border-l-2 border-l-violet-500' : ''
                    }`}>
                    <div className="flex items-center gap-2"
                      onClick={() => {
                        if (renamingSession === session.id) return
                        setCurrentSessionId(session.id)
                        if (session.campaign_id) loadCampaignReport(String(session.campaign_id))
                        else { setCampaignReport(null); setCenterContent(null) }
                      }}
                      style={{ cursor: 'pointer' }}>
                      <div className={`w-2 h-2 rounded-full shrink-0 neu-dot ${
                        session.status === 'completed' ? 'bg-emerald-500' : session.status === 'active' ? 'bg-violet-500' : 'bg-muted-foreground/30'
                      }`} />
                      {renamingSession === session.id ? (
                        <input autoFocus value={renameValue}
                          onChange={e => setRenameValue(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') renameSession(session.id, renameValue); if (e.key === 'Escape') setRenamingSession(null) }}
                          onBlur={() => { if (renameValue.trim()) renameSession(session.id, renameValue); else setRenamingSession(null) }}
                          className="flex-1 text-[11px] px-1 py-0.5 bg-background border border-violet-500 rounded outline-none" />
                      ) : (
                        <span className="text-[11px] font-medium text-foreground truncate flex-1">{session.title}</span>
                      )}
                      {/* Menu button — REMOVIDO nesta praça: renomear/mover/apagar
                          batem em /api/marketing/sessions/:id, e aqui a lista é
                          campaign_queue (ver loadSessions). O id não é de sessão
                          nenhuma, então as três ações dariam 404 em silêncio —
                          pior ainda, "Apagar" pareceria apagar o histórico do
                          disparo. Catálogo é só leitura. */}
                    </div>
                    <div className="flex items-center gap-1.5 mt-0.5 ml-4">
                      <span className="text-[9px] text-muted-foreground">{formatDate(session.created_at)}</span>
                      {session.campaign_id && (
                        <>
                          <span className={`text-[8px] px-1 py-0.5 rounded flex items-center gap-0.5 ${
                            session.campaign_status === 'completed' ? 'bg-emerald-500/15 text-emerald-500' :
                            session.campaign_status === 'failed' ? 'bg-red-500/15 text-red-500' :
                            session.campaign_status === 'paused' ? 'bg-amber-500/15 text-amber-500' :
                            'bg-blue-500/15 text-blue-500'
                          }`}>
                            <FiBarChart2 className="w-2 h-2" />
                            {session.contacts_total || 0}
                          </span>
                          {(session.contacts_total || 0) > 0 && (
                            <span className="text-[8px] text-emerald-500 font-medium">
                              {Math.round(((session.contacts_success || 0) / session.contacts_total!) * 100)}%
                            </span>
                          )}
                        </>
                      )}
                    </div>
                    {/* Context menu dropdown */}
                    {sessionMenu === session.id && (
                      <>
                        <div className="fixed inset-0 z-[9998]" onClick={() => setSessionMenu(null)} />
                        <div className="absolute right-2 top-8 w-44 bg-card neu-pop border border-border rounded-lg z-[9999] overflow-hidden">
                          <button onClick={() => { setRenamingSession(session.id); setRenameValue(session.title); setSessionMenu(null) }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-[11px] text-foreground hover:bg-secondary transition-colors">
                            <FiEdit2 className="w-3 h-3" /> Renomear
                          </button>
                          {folders.length > 0 && (
                            <div className="border-t border-border/50">
                              {folders.filter(f => f !== session.folder).map(f => (
                                <button key={f} onClick={() => moveToFolder(session.id, f)}
                                  className="w-full flex items-center gap-2 px-3 py-1.5 text-[10px] text-muted-foreground hover:bg-secondary transition-colors">
                                  <FiFolder className="w-3 h-3" /> Mover para {f}
                                </button>
                              ))}
                              {session.folder && (
                                <button onClick={() => moveToFolder(session.id, null)}
                                  className="w-full flex items-center gap-2 px-3 py-1.5 text-[10px] text-muted-foreground hover:bg-secondary transition-colors">
                                  <FiX className="w-3 h-3" /> Remover da pasta
                                </button>
                              )}
                            </div>
                          )}
                          <button onClick={() => {
                            setSessionMenu(null)
                            setShowNewFolder(true)
                            setFolderInput('')
                          }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-[11px] text-foreground hover:bg-secondary transition-colors border-t border-border/50">
                            <FiFolder className="w-3 h-3 text-violet-500" /> Nova pasta...
                          </button>
                          <button onClick={() => { setSessionMenu(null); deleteSession(session.id) }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-[11px] text-red-500 hover:bg-red-500/10 transition-colors border-t border-border/50">
                            <FiTrash2 className="w-3 h-3" /> Excluir
                          </button>
                        </div>
                      </>
                    )}
                    {/* New folder inline input */}
                    {showNewFolder && sessionMenu === null && renamingSession === null && currentSessionId === session.id && (
                      <div className="mt-1 ml-4 flex gap-1">
                        <input autoFocus value={folderInput} onChange={e => setFolderInput(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter' && folderInput.trim()) { moveToFolder(session.id, folderInput.trim()); setShowNewFolder(false) }; if (e.key === 'Escape') setShowNewFolder(false) }}
                          placeholder="Nome da pasta..."
                          className="flex-1 text-[10px] px-1.5 py-0.5 bg-background border border-border rounded outline-none focus:ring-1 focus:ring-violet-500" />
                        <button onClick={() => { if (folderInput.trim()) { moveToFolder(session.id, folderInput.trim()); setShowNewFolder(false) } }}
                          className="text-[10px] px-1.5 py-0.5 bg-violet-500 text-white rounded">OK</button>
                      </div>
                    )}
                  </div>
                )

                return (
                  <>
                    {/* Folder tabs */}
                    {folders.length > 0 && (
                      <div className="px-2 py-1.5 border-b border-border flex gap-1 flex-wrap">
                        <button onClick={() => setSelectedFolder(null)}
                          className={`text-[9px] px-2 py-0.5 rounded-full transition-colors ${!selectedFolder ? 'bg-violet-500 text-white' : 'bg-secondary text-muted-foreground hover:text-foreground'}`}>
                          Todas
                        </button>
                        {folders.map(f => (
                          <button key={f} onClick={() => setSelectedFolder(f)}
                            className={`text-[9px] px-2 py-0.5 rounded-full transition-colors flex items-center gap-0.5 ${selectedFolder === f ? 'bg-violet-500 text-white' : 'bg-secondary text-muted-foreground hover:text-foreground'}`}>
                            <FiFolder className="w-2 h-2" /> {f}
                          </button>
                        ))}
                      </div>
                    )}
                    {/* Sessions list */}
                    {(selectedFolder ? filteredSessions.filter(s => s.folder === selectedFolder) : noFolder).map(renderSession)}
                    {/* Folder groups (when no folder filter) */}
                    {!selectedFolder && folders.map(f => {
                      const folderSessions = filteredSessions.filter(s => s.folder === f)
                      if (folderSessions.length === 0) return null
                      return (
                        <div key={f}>
                          <div className="px-3 py-1 bg-secondary/30 border-b border-border flex items-center gap-1.5">
                            <FiFolder className="w-3 h-3 text-violet-500" />
                            <span className="text-[10px] font-medium text-foreground">{f}</span>
                            <span className="text-[9px] text-muted-foreground ml-auto">{folderSessions.length}</span>
                          </div>
                          {folderSessions.map(renderSession)}
                        </div>
                      )
                    })}
                  </>
                )
              })()
            )}

          </div>
        </div>
      ) : (
        /* Collapsed sidebar */
        <div className="w-10 border-r border-border flex flex-col items-center py-3 bg-card neu-bar shrink-0 gap-2">
          <button onClick={() => setSidebarOpen(true)} className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors" title="Expandir painel">
            <FiSidebar className="w-4 h-4" />
          </button>
          <div className="w-5 h-5 rounded bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
            <FiTarget className="w-2.5 h-2.5 text-white" />
          </div>
        </div>
      )}

      {/* ═══ CENTER AREA (flex-[7]) ═══ */}
      <div className="flex-[7] flex flex-col min-w-0">
        {/* Top bar */}
        <div className="flex items-center gap-2 px-3 py-1.5 border-b border-border bg-card neu-bar shrink-0">
          <span className="text-xs text-muted-foreground flex items-center gap-1.5">
            <FiTarget className="w-3.5 h-3.5 text-violet-500" />
            <span className="font-medium text-foreground">Marketing</span>
            {centerContent?.action && (
              <>
                <FiChevronRight className="w-3 h-3" />
                <span>{
                  centerContent.action === 'show_templates' ? 'Templates' :
                  centerContent.action === 'execute_campaign' ? 'Executando...' :
                  centerContent.action === 'show_report' ? 'Relatorio' :
                  centerContent.action === 'campaign_report' ? 'Relatorio da Campanha' :
                  centerContent.action === 'show_audience' ? 'Audiencia' :
                  ''
                }</span>
              </>
            )}
          </span>
          <div className="flex-1" />
          {centerContent && (
            <button onClick={() => setCenterContent(null)}
              className="text-[10px] text-muted-foreground hover:text-foreground px-2 py-0.5 rounded hover:bg-secondary transition-colors">
              Limpar
            </button>
          )}
        </div>

        {/* Dynamic center content */}
        <div className="flex-1 overflow-auto">
          {!centerContent ? (
            /* ═══ RELATÓRIOS — LISTA DE CAMPANHAS ═══
                Quando nenhuma campanha está aberta, mostra cards com as
                últimas campanhas (sessions que têm campaign_id). Clicar
                num card carrega o relatório completo no centro. */
            (() => {
              const campaignSessions = sessions.filter(s => s.campaign_id)
              if (sessionsLoading) {
                return (
                  <div className="flex flex-col items-center justify-center h-full gap-3">
                    <FiLoader className="w-6 h-6 animate-spin text-violet-500" />
                    <span className="text-sm text-muted-foreground">Carregando campanhas...</span>
                  </div>
                )
              }
              if (campaignSessions.length === 0) {
                return (
                  <div className="flex flex-col items-center justify-center h-full px-8 text-center">
                    <div className="w-16 h-16 rounded-2xl bg-secondary flex items-center justify-center mb-4">
                      <FiBarChart2 className="w-8 h-8 text-muted-foreground/50" />
                    </div>
                    <span className="text-base font-semibold text-foreground mb-1">Nenhuma campanha ainda</span>
                    {/* Repaginada 2026-07-15: disparo saiu deste painel — sem botão de
                        navegação pra aba dormente (era a única porta de volta pro wizard). */}
                    <p className="text-sm text-muted-foreground max-w-md leading-relaxed">
                      As campanhas disparadas aparecem aqui pra análise — KPIs, contato a
                      contato, exportação PDF e resumo IA. Os disparos são feitos fora
                      deste painel.
                    </p>
                  </div>
                )
              }
              // ═══ CATÁLOGO DE DISPAROS (praça Planalto) ═══
              // Agrupado por DIA, com os testes recolhidos: o teste usa o mesmo
              // template do disparo real, então lado a lado eles ficavam
              // indistinguíveis — e o ensaio de 1 contato competia visualmente
              // com o disparo de 29.
              const diaChave = (iso: string) => {
                const d = new Date(iso)
                return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
              }
              const rotuloDoDia = (chave: string) => {
                const [a, m, d] = chave.split('-').map(Number)
                const data = new Date(a, m - 1, d)
                const hoje = new Date()
                const zerar = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime()
                const dias = Math.round((zerar(hoje) - zerar(data)) / 86400000)
                const extenso = data.toLocaleDateString('pt-BR', { day: 'numeric', month: 'long' })
                if (dias === 0) return `Hoje · ${extenso}`
                if (dias === 1) return `Ontem · ${extenso}`
                if (data.getFullYear() !== hoje.getFullYear()) {
                  return data.toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' })
                }
                return extenso
              }
              const hora = (iso: string) =>
                new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })

              // Ordem dos dias preservada: a lista já vem por created_at DESC.
              const dias: { chave: string; reais: MarketingSession[]; testes: MarketingSession[] }[] = []
              for (const c of campaignSessions) {
                const chave = diaChave(c.created_at)
                let grupo = dias.find(g => g.chave === chave)
                if (!grupo) { grupo = { chave, reais: [], testes: [] }; dias.push(grupo) }
                ;(c.is_test ? grupo.testes : grupo.reais).push(c)
              }

              const CardCampanha = ({ c, compacto }: { c: MarketingSession; compacto?: boolean }) => {
                const enviados = c.contacts_success || 0
                const total = c.contacts_total || 0
                const falhas = c.contacts_failed || 0
                const entregues = c.delivered || 0
                const lidas = c.read || 0
                // Base da taxa é o ENVIADO, não o total da lista: contato que
                // nunca saiu não pode contar como entrega perdida.
                const pctEntrega = enviados > 0 ? Math.round((entregues / enviados) * 100) : 0
                const emAndamento = c.campaign_status !== 'completed' && c.campaign_status !== 'failed'
                const editando = renomeando === c.id
                return (
                  <div
                    onClick={() => { if (!editando) { setCurrentSessionId(c.id); loadCampaignReport(String(c.campaign_id)) } }}
                    className={`text-left bg-card neu-item rounded-xl border border-border ${compacto ? 'p-3' : 'p-4'} hover:border-violet-500/50 transition-all group cursor-pointer`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        {editando ? (
                          <input autoFocus value={renomeandoValor}
                            onClick={e => e.stopPropagation()}
                            onChange={e => setRenomeandoValor(e.target.value)}
                            onKeyDown={e => {
                              if (e.key === 'Enter') renomearCampanha(c.id, renomeandoValor)
                              if (e.key === 'Escape') setRenomeando(null)
                            }}
                            onBlur={() => renomearCampanha(c.id, renomeandoValor)}
                            className="w-full text-sm font-semibold px-1.5 py-0.5 bg-background border border-violet-500 rounded outline-none" />
                        ) : (
                          <div className="flex items-center gap-1.5 min-w-0">
                            <span className="text-sm font-semibold text-foreground truncate">{c.title}</span>
                            {c.is_test && (
                              <span className="text-[9px] px-1.5 py-0.5 rounded-full font-medium bg-amber-500/15 text-amber-600 dark:text-amber-400 shrink-0">
                                teste
                              </span>
                            )}
                            <button
                              onClick={e => { e.stopPropagation(); setRenomeandoValor(c.title); setRenomeando(c.id) }}
                              title="Renomear campanha"
                              className="p-0.5 rounded opacity-0 group-hover:opacity-100 hover:bg-secondary text-muted-foreground transition-all shrink-0">
                              <FiEdit2 className="w-3 h-3" />
                            </button>
                          </div>
                        )}
                        <div className="text-[10px] text-muted-foreground mt-0.5 truncate">
                          {hora(c.created_at)}
                          {c.template_name ? ` · ${c.template_name}` : ''}
                        </div>
                      </div>
                      {emAndamento && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full font-medium shrink-0 bg-blue-500/15 text-blue-600 dark:text-blue-400">
                          {c.campaign_status === 'paused' ? 'Pausada' : 'Em andamento'}
                        </span>
                      )}
                      {c.campaign_status === 'failed' && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full font-medium shrink-0 bg-red-500/15 text-red-600 dark:text-red-400">
                          Falhou
                        </span>
                      )}
                    </div>

                    <div className="grid grid-cols-3 gap-2 mt-3">
                      {[
                        { valor: enviados, rotulo: 'Envios', cor: 'text-foreground' },
                        { valor: entregues, rotulo: 'Entregues', cor: 'text-emerald-500' },
                        { valor: lidas, rotulo: 'Lidas', cor: 'text-violet-500' },
                      ].map(m => (
                        <div key={m.rotulo} className="text-center">
                          <div className={`text-base font-bold ${m.cor}`}>{m.valor}</div>
                          <div className="text-[8px] text-muted-foreground uppercase tracking-wider">{m.rotulo}</div>
                        </div>
                      ))}
                    </div>

                    <div className="mt-3">
                      <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
                        <div className="h-full bg-emerald-500 rounded-full transition-all"
                          style={{ width: `${pctEntrega}%` }} />
                      </div>
                      <div className="flex items-center gap-2 mt-1.5">
                        <span className="text-[9px] text-muted-foreground">{pctEntrega}% entregue</span>
                        {falhas > 0 && (
                          <span className="text-[9px] text-red-500">{falhas} {falhas === 1 ? 'falha' : 'falhas'}</span>
                        )}
                        {total !== enviados && (
                          <span className="text-[9px] text-muted-foreground">de {total} na lista</span>
                        )}
                        <span className="ml-auto text-[9px] text-violet-500 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                          <FiBarChart2 className="w-3 h-3" /> Abrir relatório
                        </span>
                      </div>
                    </div>
                  </div>
                )
              }

              return (
                <div className="p-4 space-y-5">
                  <div className="flex items-center gap-2">
                    <h2 className="text-sm font-semibold text-foreground">Disparos</h2>
                    <span className="text-[10px] text-muted-foreground">({campaignSessions.length})</span>
                    <button onClick={loadSessions}
                      className="ml-auto p-1.5 rounded-lg hover:bg-secondary text-muted-foreground transition-colors"
                      title="Recarregar">
                      <FiRefreshCw className={`w-3 h-3 ${sessionsLoading ? 'animate-spin' : ''}`} />
                    </button>
                  </div>

                  {dias.map(dia => {
                    const testesAbertos = diasComTesteAberto.includes(dia.chave)
                    return (
                      <div key={dia.chave} className="space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                            {rotuloDoDia(dia.chave)}
                          </span>
                          <div className="flex-1 h-px bg-border" />
                        </div>

                        {dia.reais.length > 0 && (
                          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                            {dia.reais.map(c => <CardCampanha key={c.id} c={c} />)}
                          </div>
                        )}

                        {dia.testes.length > 0 && (
                          <div>
                            <button
                              onClick={() => setDiasComTesteAberto(prev =>
                                prev.includes(dia.chave) ? prev.filter(k => k !== dia.chave) : [...prev, dia.chave])}
                              className="flex items-center gap-1.5 text-[10px] text-muted-foreground hover:text-foreground transition-colors py-1">
                              <FiChevronDown className={`w-3 h-3 transition-transform ${testesAbertos ? '' : '-rotate-90'}`} />
                              {dia.testes.length} {dia.testes.length === 1 ? 'teste' : 'testes'} neste dia
                            </button>
                            {testesAbertos && (
                              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 mt-2 opacity-75">
                                {dia.testes.map(c => <CardCampanha key={c.id} c={c} compacto />)}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )
            })()
          ) : centerContent.action === 'show_report' ? (
            /* ═══ CAMPAIGN REPORT ═══ */
            <div className="p-4 space-y-4">
              <div className="bg-card neu-card rounded-xl border border-border p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-violet-500/15 flex items-center justify-center shrink-0">
                    <FiBarChart2 className="w-5 h-5 text-violet-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-base font-bold text-foreground leading-tight">
                      {(centerContent.data.title as string) || 'Relatorio de Campanha'}
                    </h2>
                    <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                      {(centerContent.data.summary as string) || ''}
                    </p>
                  </div>
                </div>
              </div>
              {/* KPIs */}
              {(centerContent.data.kpis as { label: string; value: string; color?: string }[])?.length > 0 && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  {(centerContent.data.kpis as { label: string; value: string; color?: string }[]).map((kpi, i) => (
                    <div key={i} className="bg-card neu-card rounded-xl border border-border p-3">
                      <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">{kpi.label}</span>
                      <div className="text-xl font-bold mt-1" style={{ color: kpi.color || CHART_COLORS[i] }}>{kpi.value}</div>
                    </div>
                  ))}
                </div>
              )}
              {/* Charts */}
              {(centerContent.data.charts as { type: string; title: string; data: Record<string, unknown>[]; dataKey: string; categoryKey: string }[])?.length > 0 && (
                <div className="grid gap-4 grid-cols-1 lg:grid-cols-2">
                  {(centerContent.data.charts as { type: string; title: string; data: Record<string, unknown>[]; dataKey: string; categoryKey: string }[]).map((chart, ci) => (
                    <div key={ci} className="bg-card neu-card rounded-xl border border-border p-4">
                      <h3 className="text-sm font-semibold text-foreground mb-3">{chart.title}</h3>
                      <div style={{ height: chart.type === 'pie' ? 260 : 220 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          {chart.type === 'pie' ? (
                            <PieChart>
                              <Pie data={chart.data} dataKey={chart.dataKey || 'value'} nameKey={chart.categoryKey || 'name'}
                                cx="50%" cy="50%" outerRadius={90} innerRadius={45} paddingAngle={2}>
                                {chart.data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                              </Pie>
                              <Tooltip />
                            </PieChart>
                          ) : (
                            <BarChart data={chart.data}>
                              <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
                              <XAxis dataKey={chart.categoryKey || 'name'} tick={{ fontSize: 10 }} />
                              <YAxis tick={{ fontSize: 10 }} />
                              <Tooltip />
                              <Bar dataKey={chart.dataKey || 'value'} radius={[4, 4, 0, 0]}>
                                {chart.data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                              </Bar>
                            </BarChart>
                          )}
                        </ResponsiveContainer>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

          ) : centerContent.action === 'campaign_report' ? (
            /* ═══ CAMPAIGN REPORT (SINTÉTICO + ANALÍTICO + PDF) ═══ */
            reportLoading ? (
              <div className="flex flex-col items-center justify-center h-full gap-3">
                <FiLoader className="w-6 h-6 animate-spin text-violet-500" />
                <span className="text-sm text-muted-foreground">Carregando relatório...</span>
              </div>
            ) : campaignReport ? (
              <div className="p-4 space-y-4 overflow-auto">
                {/* Header */}
                <div className="bg-card neu-card rounded-2xl border border-border overflow-hidden">
                  <div className="bg-gradient-to-r from-violet-500 to-purple-600 px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                        <FiBarChart2 className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1">
                        <h2 className="text-sm font-bold text-white">Relatório da Campanha</h2>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-[10px] text-white/80">{campaignReport.campaign.templateName}</span>
                          <span className="text-[10px] text-white/60">•</span>
                          <span className="text-[10px] text-white/60">{(campaignReport.campaign.platform || 'meta').toUpperCase()}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-[10px] px-2 py-1 rounded-full font-medium ${
                          campaignReport.campaign.status === 'completed' ? 'bg-emerald-500/20 text-emerald-200' :
                          campaignReport.campaign.status === 'failed' ? 'bg-red-500/20 text-red-200' :
                          'bg-white/20 text-white/80'
                        }`}>
                          {campaignReport.campaign.status === 'completed' ? 'Concluída' :
                           campaignReport.campaign.status === 'failed' ? 'Falhou' : campaignReport.campaign.status}
                        </span>
                        <button onClick={summarizeReport} disabled={reportSummaryLoading}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-medium rounded-lg bg-white/20 hover:bg-white/30 disabled:opacity-50 text-white transition-colors"
                          title="Resumir com IA">
                          {reportSummaryLoading ? <FiLoader className="w-3 h-3 animate-spin" /> : <FiZap className="w-3 h-3" />} Resumir IA
                        </button>
                        <button onClick={downloadReportXlsx}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-medium rounded-lg bg-white/20 hover:bg-white/30 text-white transition-colors"
                          title="Baixar XLSX (Excel)">
                          <FiDownload className="w-3 h-3" /> XLSX
                        </button>
                        <button onClick={downloadReportPdf}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-medium rounded-lg bg-white/20 hover:bg-white/30 text-white transition-colors"
                          title="Baixar PDF">
                          <FiDownload className="w-3 h-3" /> PDF
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* AI Summary block */}
                  {reportSummary && (
                    <div className="px-5 py-3 border-t border-border bg-violet-500/5">
                      <div className="flex items-start gap-2">
                        <FiZap className="w-3.5 h-3.5 text-violet-500 shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <div className="text-[10px] font-semibold text-violet-600 dark:text-violet-400 uppercase tracking-wider mb-1">Resumo IA</div>
                          <p className="text-[11px] text-foreground leading-relaxed whitespace-pre-wrap">{reportSummary}</p>
                        </div>
                        <button onClick={() => setReportSummary(null)} className="text-muted-foreground hover:text-foreground" title="Fechar">
                          <FiX className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Campaign Info Row */}
                  <div className="flex divide-x divide-border text-center">
                    {[
                      { label: 'Início', value: campaignReport.campaign.startedAt ? new Date(campaignReport.campaign.startedAt).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-' },
                      { label: 'Fim', value: campaignReport.campaign.completedAt ? new Date(campaignReport.campaign.completedAt).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-' },
                      { label: 'Tipo', value: campaignReport.campaign.messageType || 'template' },
                      { label: 'Delay', value: `${(campaignReport.campaign.delayMs || 2000) / 1000}s` },
                    ].map((item, i) => (
                      <div key={i} className="flex-1 px-3 py-2">
                        <div className="text-[9px] text-muted-foreground uppercase tracking-wider">{item.label}</div>
                        <div className="text-[11px] font-medium text-foreground mt-0.5">{item.value}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Tab Switcher */}
                <div className="flex gap-1 bg-secondary/50 rounded-lg p-1">
                  {([
                    { key: 'sintetico' as const, icon: FiBarChart2, label: 'Sintético' },
                    { key: 'analitico' as const, icon: FiUsers, label: 'Analítico' },
                  ]).map(tab => (
                    <button key={tab.key}
                      onClick={() => setReportTab(tab.key)}
                      className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-all ${
                        reportTab === tab.key
                          ? 'bg-card text-foreground neu-relief'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}>
                      <tab.icon className="w-3.5 h-3.5" />
                      {tab.label}
                    </button>
                  ))}
                </div>

                {/* ─── SINTÉTICO TAB ─── */}
                {reportTab === 'sintetico' && (
                  <div className="space-y-4">
                    {/* KPI Cards */}
                    <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
                      {[
                        { label: 'Total', value: campaignReport.metrics.total, color: '#6b7280', rate: null },
                        { label: 'Enviados', value: campaignReport.metrics.sent, color: '#3b82f6', rate: `${campaignReport.metrics.successRate}%` },
                        { label: 'Entregues', value: campaignReport.metrics.delivered, color: '#10b981', rate: `${campaignReport.metrics.deliveryRate}%` },
                        { label: 'Lidos', value: campaignReport.metrics.read, color: '#7c3aed', rate: `${campaignReport.metrics.readRate}%` },
                        { label: 'Respostas', value: campaignReport.metrics.responses, color: '#f59e0b', rate: `${campaignReport.metrics.responseRate}%` },
                        { label: 'Falhas', value: campaignReport.metrics.failed, color: '#ef4444', rate: null },
                      ].map((kpi, i) => (
                        <div key={i} className="bg-card neu-card rounded-xl border border-border p-3 text-center">
                          <div className="text-2xl font-bold" style={{ color: kpi.color }}>{kpi.value}</div>
                          <div className="text-[9px] text-muted-foreground uppercase tracking-wider mt-0.5">{kpi.label}</div>
                          {kpi.rate && <div className="text-[10px] text-muted-foreground mt-0.5">{kpi.rate}</div>}
                        </div>
                      ))}
                    </div>

                    {/* Charts */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {/* Funnel / Status Distribution Pie */}
                      <div className="bg-card neu-card rounded-xl border border-border p-4">
                        <h3 className="text-sm font-semibold text-foreground mb-3">Distribuição por Status</h3>
                        <div style={{ height: 260 }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={Object.entries(campaignReport.statusDistribution).map(([name, value]) => ({ name, value }))}
                                dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} innerRadius={45} paddingAngle={2}
                                label={({ name, value }) => `${name} (${value})`}>
                                {Object.keys(campaignReport.statusDistribution).map((_, i) => (
                                  <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                                ))}
                              </Pie>
                              <Tooltip />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                      </div>

                      {/* Funnel bar chart */}
                      <div className="bg-card neu-card rounded-xl border border-border p-4">
                        <h3 className="text-sm font-semibold text-foreground mb-3">Funil de Entrega</h3>
                        <div style={{ height: 260 }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={[
                              { name: 'Enviados', value: campaignReport.metrics.sent, fill: '#3b82f6' },
                              { name: 'Entregues', value: campaignReport.metrics.delivered, fill: '#10b981' },
                              { name: 'Lidos', value: campaignReport.metrics.read, fill: '#7c3aed' },
                              { name: 'Respostas', value: campaignReport.metrics.responses, fill: '#f59e0b' },
                            ]}>
                              <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
                              <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                              <YAxis tick={{ fontSize: 10 }} />
                              <Tooltip />
                              <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                                {[
                                  { fill: '#3b82f6' }, { fill: '#10b981' }, { fill: '#7c3aed' }, { fill: '#f59e0b' },
                                ].map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    </div>

                    {/* Template Info */}
                    <div className="bg-card neu-card rounded-xl border border-border p-4">
                      <h3 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                        <FiFileText className="w-4 h-4 text-violet-500" /> Template Utilizado
                      </h3>
                      <div className="bg-secondary/30 rounded-lg px-4 py-3">
                        <div className="text-xs font-mono text-foreground">{campaignReport.campaign.templateName}</div>
                        <div className="flex items-center gap-3 mt-1.5 text-[10px] text-muted-foreground">
                          <span>Tipo: {campaignReport.campaign.messageType || 'template'}</span>
                          <span>Plataforma: {(campaignReport.campaign.platform || 'meta').toUpperCase()}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* ─── ANALÍTICO TAB ─── */}
                {reportTab === 'analitico' && (
                  <div className="space-y-4">

                  {/* Crosscheck: identidade dos destinatários (nome/grupo/distrito via cadastro) */}
                  {campaignReport.crosscheck && (
                    <>
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                        {(() => {
                          const q = campaignReport.crosscheck.qualidade
                          const pct = (n: number) => q.total > 0 ? `${Math.round((n / q.total) * 100)}%` : '0%'
                          return [
                            { label: 'Total disparado', value: q.total, color: '#6b7280', rate: null as string | null },
                            { label: 'Identificados', value: q.identificados, color: '#10b981', rate: pct(q.identificados) },
                            { label: 'Ambíguos', value: q.ambiguos, color: '#f59e0b', rate: pct(q.ambiguos) },
                            { label: 'Sem cadastro', value: q.semCadastro, color: '#ef4444', rate: pct(q.semCadastro) },
                          ].map((k, i) => (
                            <div key={i} className="bg-card neu-card rounded-xl border border-border p-3 text-center">
                              <div className="text-2xl font-bold" style={{ color: k.color }}>{k.value}</div>
                              <div className="text-[9px] text-muted-foreground uppercase tracking-wider mt-0.5">{k.label}</div>
                              {k.rate && <div className="text-[10px] text-muted-foreground mt-0.5">{k.rate}</div>}
                            </div>
                          ))
                        })()}
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {([
                          { title: 'Por Distrito', rows: campaignReport.crosscheck.porDistrito },
                          { title: 'Por Grupo', rows: campaignReport.crosscheck.porGrupo },
                        ]).map((blk, bi) => (
                          <div key={bi} className="bg-card neu-card rounded-xl border border-border overflow-hidden">
                            <div className="px-4 py-2.5 border-b border-border flex items-center gap-2">
                              <FiBarChart2 className="w-4 h-4 text-violet-500" />
                              <h3 className="text-sm font-semibold text-foreground">{blk.title}</h3>
                              <span className="text-[10px] text-muted-foreground ml-auto">{blk.rows.length}</span>
                            </div>
                            <div className="overflow-auto max-h-[280px]">
                              <table className="w-full text-xs border-collapse">
                                <thead className="sticky top-0 bg-card z-10 neu-thead">
                                  <tr className="text-[10px] uppercase tracking-wider text-muted-foreground">
                                    <th className="px-3 py-2 text-left font-semibold border-b border-border">{blk.title.replace('Por ', '')}</th>
                                    <th className="px-3 py-2 text-right font-semibold border-b border-border">Disparos</th>
                                    <th className="px-3 py-2 text-right font-semibold border-b border-border">Entregue</th>
                                    <th className="px-3 py-2 text-right font-semibold border-b border-border">Lido</th>
                                    <th className="px-3 py-2 text-right font-semibold border-b border-border">Resp.</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {blk.rows.map((r, i) => (
                                    <tr key={i} className={`hover:bg-secondary/50 ${i % 2 === 0 ? '' : 'bg-muted/30'}`}>
                                      <td className="px-3 py-1.5 text-foreground border-b border-border/30 font-medium">{r.nome}</td>
                                      <td className="px-3 py-1.5 text-right text-foreground border-b border-border/30 font-semibold">{r.total}</td>
                                      <td className="px-3 py-1.5 text-right text-emerald-600 border-b border-border/30">{r.entregue || '-'}</td>
                                      <td className="px-3 py-1.5 text-right text-violet-600 border-b border-border/30">{r.lido || '-'}</td>
                                      <td className="px-3 py-1.5 text-right text-amber-600 border-b border-border/30">{r.respondeu || '-'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  <div className="bg-card neu-card rounded-xl border border-border overflow-hidden">
                    <div className="px-4 py-3 border-b border-border flex items-center gap-2">
                      <FiUsers className="w-4 h-4 text-violet-500" />
                      <h3 className="text-sm font-semibold text-foreground">Detalhamento por Contato</h3>
                      <span className="text-[10px] text-muted-foreground ml-auto">{campaignReport.contacts.length} contatos</span>
                      <button onClick={downloadReportXlsx}
                        className="flex items-center gap-1 px-2 py-1 text-[10px] rounded-md bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20 transition-colors">
                        <FiDownload className="w-3 h-3" /> XLSX
                      </button>
                      <button onClick={downloadReportPdf}
                        className="flex items-center gap-1 px-2 py-1 text-[10px] rounded-md bg-violet-500/10 text-violet-500 hover:bg-violet-500/20 transition-colors">
                        <FiDownload className="w-3 h-3" /> PDF
                      </button>
                    </div>
                    <div className="overflow-auto max-h-[500px]">
                      <table className="w-full text-xs border-collapse">
                        <thead className="sticky top-0 bg-card z-10 neu-thead">
                          <tr>
                            <th className="px-3 py-2 text-left font-semibold text-muted-foreground border-b border-border">#</th>
                            <th className="px-3 py-2 text-left font-semibold text-muted-foreground border-b border-border">Nome</th>
                            <th className="px-3 py-2 text-left font-semibold text-muted-foreground border-b border-border">Telefone</th>
                            <th className="px-3 py-2 text-left font-semibold text-muted-foreground border-b border-border">Consultor</th>
                            <th className="px-3 py-2 text-left font-semibold text-muted-foreground border-b border-border">Grupo</th>
                            <th className="px-3 py-2 text-left font-semibold text-muted-foreground border-b border-border">Distrito</th>
                            <th className="px-3 py-2 text-left font-semibold text-muted-foreground border-b border-border">Status</th>
                            <th className="px-3 py-2 text-left font-semibold text-muted-foreground border-b border-border">Enviado</th>
                            <th className="px-3 py-2 text-left font-semibold text-muted-foreground border-b border-border">Entregue</th>
                            <th className="px-3 py-2 text-left font-semibold text-muted-foreground border-b border-border">Lido</th>
                            <th className="px-3 py-2 text-left font-semibold text-muted-foreground border-b border-border">Resposta</th>
                          </tr>
                        </thead>
                        <tbody>
                          {campaignReport.contacts.map((ct, i) => {
                            const st = ct.latestStatus || ct.status
                            const stColor = st === 'read' ? 'text-violet-500' : st === 'delivered' ? 'text-emerald-500' : st === 'sent' ? 'text-blue-500' : st === 'failed' ? 'text-red-500' : 'text-muted-foreground'
                            const stLabel = st === 'sent' ? 'Enviado' : st === 'delivered' ? 'Entregue' : st === 'read' ? 'Lido' : st === 'failed' ? 'Falha' : st === 'pending' ? 'Pendente' : st
                            const fmtDt = (d: string | null) => d ? new Date(d).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-'
                            return (
                              <tr key={i} className={`hover:bg-secondary/50 ${i % 2 === 0 ? '' : 'bg-muted/30'}`}>
                                <td className="px-3 py-1.5 text-muted-foreground border-b border-border/30">{i + 1}</td>
                                <td className="px-3 py-1.5 text-foreground border-b border-border/30 font-medium">{ct.contactName || '-'}</td>
                                <td className="px-3 py-1.5 font-mono text-foreground border-b border-border/30">{ct.phone}</td>
                                <td className="px-3 py-1.5 border-b border-border/30">
                                  {ct.semCadastro ? (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-500/10 text-red-500">sem cadastro</span>
                                  ) : (
                                    <span className="flex items-center gap-1.5">
                                      <span className="text-foreground truncate max-w-[150px]" title={ct.cadastroNome || ''}>{ct.cadastroNome || '-'}</span>
                                      {ct.codigo && <span className="text-[9px] text-muted-foreground font-mono">#{ct.codigo}</span>}
                                      {ct.matchAmbiguo && <span className="text-[9px] px-1 py-0.5 rounded bg-amber-500/15 text-amber-600" title="Telefone bate com mais de um cadastro (linha compartilhada)">ambíguo</span>}
                                    </span>
                                  )}
                                </td>
                                <td className="px-3 py-1.5 text-muted-foreground border-b border-border/30">{ct.grupo || '-'}</td>
                                <td className="px-3 py-1.5 text-muted-foreground border-b border-border/30">{ct.distrito || '-'}</td>
                                <td className={`px-3 py-1.5 border-b border-border/30 font-medium ${stColor}`}>
                                  <span className="flex items-center gap-1">
                                    {st === 'failed' ? <FiAlertCircle className="w-3 h-3" /> :
                                     st === 'read' ? <FiEye className="w-3 h-3" /> :
                                     st === 'delivered' ? <FiCheck className="w-3 h-3" /> :
                                     st === 'sent' ? <FiSend className="w-2.5 h-2.5" /> :
                                     <FiClock className="w-3 h-3" />}
                                    {stLabel}
                                  </span>
                                </td>
                                <td className="px-3 py-1.5 text-muted-foreground border-b border-border/30 text-[10px]">{fmtDt(ct.sentAt)}</td>
                                <td className="px-3 py-1.5 text-muted-foreground border-b border-border/30 text-[10px]">{fmtDt(ct.deliveredAt)}</td>
                                <td className="px-3 py-1.5 text-muted-foreground border-b border-border/30 text-[10px]">{fmtDt(ct.readAt)}</td>
                                <td className="px-3 py-1.5 border-b border-border/30 text-[10px]">
                                  {ct.responseContent ? (
                                    <span className="text-amber-500 truncate block max-w-[150px]" title={ct.responseContent}>{ct.responseContent}</span>
                                  ) : ct.errorMessage ? (
                                    <span className="text-red-400 truncate block max-w-[150px]" title={ct.errorMessage}>{ct.errorMessage}</span>
                                  ) : '-'}
                                </td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                <FiAlertCircle className="w-8 h-8 mb-2 opacity-30" />
                <span className="text-sm">Não foi possível carregar o relatório</span>
              </div>
            )

          ) : centerContent.action === 'show_audience' ? (
            /* ═══ AUDIENCE TABLE ═══ */
            <div className="p-4">
              <div className="bg-card neu-card rounded-2xl border border-border overflow-hidden">
                <div className="px-4 py-3 border-b border-border flex items-center gap-2">
                  <FiUsers className="w-4 h-4 text-blue-500" />
                  <h2 className="text-sm font-semibold text-foreground">Audiencia</h2>
                  <span className="text-[10px] text-muted-foreground ml-auto">{(centerContent.data.total as number) || (centerContent.data.contacts as Contact[])?.length || 0} contatos</span>
                </div>
                <div className="overflow-auto max-h-96">
                  <table className="w-full text-xs border-collapse">
                    <thead className="sticky top-0 bg-card z-10 neu-thead">
                      <tr>
                        <th className="px-3 py-1.5 text-left font-semibold text-muted-foreground border-b border-border">#</th>
                        <th className="px-3 py-1.5 text-left font-semibold text-muted-foreground border-b border-border">Nome</th>
                        <th className="px-3 py-1.5 text-left font-semibold text-muted-foreground border-b border-border">Telefone</th>
                      </tr>
                    </thead>
                    <tbody>
                      {((centerContent.data.contacts as Contact[]) || []).slice(0, 100).map((c, i) => (
                        <tr key={i} className={`hover:bg-secondary/50 ${i % 2 === 0 ? '' : 'bg-muted/50'}`}>
                          <td className="px-3 py-1 text-muted-foreground border-b border-border/50">{i + 1}</td>
                          <td className="px-3 py-1 text-foreground border-b border-border/50">{c.name}</td>
                          <td className="px-3 py-1 font-mono text-foreground border-b border-border/50">{c.phone}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      </div>
      )}{/* fim do ternário activeTab === 'meta' ? placeholder : sidebar+centro */}

      {/* Modal de confirmação de exclusão de sessão com campanha */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <div className="absolute inset-0 glass-overlay" onClick={() => setDeleteConfirm(null)} />
          <div className="relative z-10 glass-panel neu-drawer neu-forms rounded-2xl w-[420px] overflow-hidden">
            <div className="px-5 py-4 border-b border-border bg-amber-500/5">
              <div className="flex items-center gap-2.5">
                <div className="w-10 h-10 rounded-xl bg-amber-500/15 flex items-center justify-center">
                  <FiAlertCircle className="w-5 h-5 text-amber-500" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground">Sessão com Campanha</h3>
                  <p className="text-[10px] text-muted-foreground">Esta sessão tem relatórios vinculados</p>
                </div>
              </div>
            </div>
            <div className="p-5 space-y-3">
              {deleteConfirm.campaign && (
                <div className="p-3 rounded-lg bg-secondary/50 border border-border/50 space-y-1">
                  <div className="flex items-center gap-2 text-[11px]">
                    <FiFileText className="w-3 h-3 text-violet-500 shrink-0" />
                    <span className="font-medium text-foreground">{deleteConfirm.campaign.templateName}</span>
                  </div>
                  <div className="flex items-center gap-3 text-[10px] text-muted-foreground ml-5">
                    <span>{deleteConfirm.campaign.contactsTotal} contatos</span>
                    <span className={deleteConfirm.campaign.status === 'completed' ? 'text-emerald-500' : 'text-amber-500'}>
                      {deleteConfirm.campaign.status}
                    </span>
                  </div>
                </div>
              )}
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                Ao excluir, as mensagens da conversa serão removidas. Os relatórios da campanha serão mantidos no sistema.
              </p>
              <div className="flex gap-2 pt-1">
                <button onClick={() => setDeleteConfirm(null)}
                  className="flex-1 px-3 py-2 text-[11px] rounded-lg border border-border hover:bg-secondary text-muted-foreground transition-colors">
                  Cancelar
                </button>
                <button onClick={() => deleteSession(deleteConfirm.id, true)}
                  className="flex-1 px-3 py-2 text-[11px] rounded-lg bg-red-500 hover:bg-red-600 text-white font-medium transition-colors">
                  Excluir Mesmo Assim
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
